/**
 * AdminBackgroundDisputes — /admin/background-checks/disputes
 * Queue of every check with an open or recent dispute. Rows open longer
 * than 30 days are flagged red. Compliance-role admins can transition
 * status + log outcome + resolution notes inline.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, Flag, RefreshCw, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TABS = [
    { key: "all", label: "All" },
    { key: "open", label: "Open" },
    { key: "under_reinvestigation", label: "Reinvestigating" },
    { key: "resolved", label: "Resolved" },
];

const StatusChip = ({ status, stale }) => (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
        status === "resolved" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40"
        : status === "under_reinvestigation" ? "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/40"
        : stale ? "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/40"
        : "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40"
    }`}>
        {stale ? <AlertTriangle size={9} /> : <Flag size={9} />}
        {stale ? "Stale · " : ""}{status}
    </span>
);

const DisputeRow = ({ row, onPatch, canEdit }) => {
    const [status, setStatus] = useState(row.dispute?.status || "open");
    const [outcome, setOutcome] = useState(row.dispute?.outcome || "");
    const [notes, setNotes] = useState(row.dispute?.resolution_notes || "");
    const [saving, setSaving] = useState(false);
    const dirty = status !== (row.dispute?.status || "open")
        || outcome !== (row.dispute?.outcome || "")
        || notes !== (row.dispute?.resolution_notes || "");

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/background-checks/disputes/${row.id}`, {
                status, outcome, resolution_notes: notes,
            });
            toast.success("Dispute updated.");
            onPatch();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card-soft p-5 space-y-3" data-testid={`bg-dispute-row-${row.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusChip status={row.dispute?.status || "open"} stale={row.dispute?.stale} />
                        <span className="text-sm font-semibold text-foreground">{row.candidate_name || row.candidate_email}</span>
                        <span className="text-xs text-muted-foreground">· {row.employer_company}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                        Opened {row.dispute?.opened_at ? new Date(row.dispute.opened_at).toLocaleString() : "—"} · {row.package_label || row.package_id}
                    </div>
                    {row.dispute?.description && (
                        <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3 text-xs italic text-foreground whitespace-pre-wrap">
                            &ldquo;{row.dispute.description}&rdquo;
                        </div>
                    )}
                </div>
                <Link to={`/admin/background-checks/${row.id}`} className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1 shrink-0" data-testid={`bg-dispute-open-${row.id}`}>
                    Open check <ArrowUpRight size={11} />
                </Link>
            </div>

            {canEdit && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`bg-dispute-status-${row.id}`}>
                        <option value="open">Open</option>
                        <option value="under_reinvestigation">Under reinvestigation</option>
                        <option value="resolved">Resolved</option>
                    </select>
                    <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`bg-dispute-outcome-${row.id}`}>
                        <option value="">Outcome…</option>
                        <option value="updated">Updated</option>
                        <option value="unchanged">Unchanged</option>
                        <option value="canceled">Canceled</option>
                        <option value="other">Other</option>
                    </select>
                    <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Resolution notes" className="md:col-span-2 rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`bg-dispute-notes-${row.id}`} />
                </div>
            )}
            {canEdit && (
                <div className="flex justify-end">
                    <button type="button" onClick={save} disabled={!dirty || saving} className="btn-green text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid={`bg-dispute-save-${row.id}`}>
                        {saving ? <><Loader2 size={11} className="animate-spin" /> Saving…</> : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default function AdminBackgroundDisputes() {
    const [tab, setTab] = useState("all");
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [threshold, setThreshold] = useState(30);
    const [loading, setLoading] = useState(true);
    const [canEdit, setCanEdit] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = tab === "all" ? {} : { status: tab };
            const { data } = await api.get("/admin/background-checks/disputes/inbox", { params });
            setRows(data.results || []);
            setCounts(data.counts || {});
            setThreshold(data.stale_threshold_days || 30);
            // Guess edit capability — if the response leaks the description field only when Compliance,
            // treat that as a proxy. Cleaner: infer from the caller's admin_role via the list endpoint.
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load disputes.");
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    // Detect edit capability by pinging the list endpoint (it returns admin_role + can_see_details)
    useEffect(() => {
        api.get("/admin/background-checks", { params: { page_size: 1 } })
            .then(({ data }) => setCanEdit(!!data.can_see_details))
            .catch(() => setCanEdit(false));
    }, []);

    return (
        <div className="container-app py-10" data-testid="bg-disputes-page">
            <div className="flex items-center gap-2 text-sm">
                <Link to="/admin/background-checks" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="bg-disputes-back">
                    <ArrowLeft size={14} /> All background checks
                </Link>
            </div>

            <header className="mt-4 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="overline inline-flex items-center gap-1"><Flag size={11} /> Disputes</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">Disputes inbox</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Candidates who challenged the accuracy of their report. Anything open longer than <b className="text-foreground">{threshold} days</b> is flagged.</p>
                </div>
                <button type="button" onClick={load} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="bg-disputes-refresh">
                    <RefreshCw size={12} /> Refresh
                </button>
            </header>

            {/* Stale banner */}
            {counts.stale > 0 && (
                <div className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3" data-testid="bg-disputes-stale-banner">
                    <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
                    <div className="text-sm text-foreground">
                        <b>{counts.stale} dispute{counts.stale === 1 ? " has" : "s have"} been open longer than {threshold} days.</b>
                        <p className="mt-1 text-xs text-muted-foreground">FCRA gives reporting agencies 30 days for a reinvestigation. Escalate to Checkr or resolve.</p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="mt-6 flex flex-wrap gap-2" data-testid="bg-disputes-tabs">
                {TABS.map((t) => {
                    const c = t.key === "all"
                        ? (counts.open || 0) + (counts.under_reinvestigation || 0) + (counts.resolved || 0)
                        : (counts[t.key] || 0);
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            data-testid={`bg-disputes-tab-${t.key}`}
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${tab === t.key ? "bg-brand-navy text-white" : "bg-muted/40 text-foreground hover:bg-muted"}`}
                        >
                            {t.label}
                            {c > 0 && <span className="rounded-full bg-black/10 dark:bg-white/10 px-1.5 py-0.5 text-[10px]">{c}</span>}
                        </button>
                    );
                })}
            </div>

            <div className="mt-6 space-y-3">
                {loading ? (
                    <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-emerald-500" /></div>
                ) : rows.length === 0 ? (
                    <div className="card-soft p-10 text-center text-sm text-muted-foreground" data-testid="bg-disputes-empty">
                        No disputes in this bucket.
                    </div>
                ) : (
                    rows.map((r) => <DisputeRow key={r.id} row={r} onPatch={load} canEdit={canEdit} />)
                )}
            </div>
        </div>
    );
}
